import React, { useCallback, useEffect, useRef, useState } from 'react';
/**
 * 横1列の棚。はみ出している側にだけ矢印を出す。
 *
 * 「出せるかどうか」を状態で持つのは、はみ出していないときに矢印を出すと
 * 押せるのに何も起きないボタンになるから。項目が2つで収まっている画面と、
 * 5つ並ぶ画面の両方で正しく振る舞わせる。
 *
 * @param {Object} props - children にカード列を渡す。
 * @returns {JSX.Element}
 */
export default function Showcase({ children, classes }) {
    const ref = useRef(null);
    const [edge, setEdge] = useState({ left: false,
        right: false });
    const sync = useCallback(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        // 端ちょうどで矢印が点滅しないよう、1px の遊びを持たせる。
        setEdge({
            left: el.scrollLeft > 1,
            right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
        });
    }, []);
    /*
     * scroll は毎フレーム飛んでくる。描画に合わせて1回だけ計算する。
     * これを外すと、描画が重い端末で入力が詰まる。
     */
    const raf = useRef(0);
    const onScroll = useCallback(() => {
        if (raf.current) {
            return;
        }
        raf.current = requestAnimationFrame(() => {
            raf.current = 0;
            sync();
        });
    }, [sync]);
    useEffect(() => {
        sync();
        window.addEventListener('resize', sync);
        return () => {
            window.removeEventListener('resize', sync);
            if (raf.current) {
                cancelAnimationFrame(raf.current);
                raf.current = 0;
            }
        };
    }, [sync, children]);
    const scrollBy = useCallback((dir) => {
        const el = ref.current;
        if (el) {
            // 1画面ぶんではなく8割。次に来るものが端に見えていた方が続きが分かる。
            el.scrollBy({ left: dir * el.clientWidth * 0.8,
                behavior: 'smooth' });
        }
    }, []);
    const arrow = (dir) => (React.createElement("button", { "aria-label": dir < 0 ? '前へ' : '次へ', className: `${classes.arrow} ${dir < 0 ? classes.arrowLeft : classes.arrowRight}`, 
        // eslint-disable-next-line react/jsx-no-bind
        onClick: () => scrollBy(dir), type: 'button' },
        React.createElement("svg", { fill: 'none', height: '18', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, viewBox: '0 0 24 24', width: '18' },
            React.createElement("path", { d: dir < 0 ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6' }))));
    return (React.createElement("div", { className: classes.shelf },
        edge.left && arrow(-1),
        React.createElement("div", { className: classes.strip, onScroll: onScroll, ref: ref }, children),
        edge.right && arrow(1)));
}
